import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APP_NAME, APP_TAGLINE, type SsoConnectionHint } from '@imsenta/shared';
import { LogoMark, LogoTile } from '@/components/Logo';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

export function SignInPage() {
  const { login, register } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'register'>('signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.get('sso_error'));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({ organisationName: '', name: '', email: '', password: '' });
  /** Connections matching the typed email domain, from home-realm discovery. */
  const [ssoOptions, setSsoOptions] = useState<SsoConnectionHint[]>([]);
  const [checkingSso, setCheckingSso] = useState(false);

  // Clear the SSO error from the address bar once it has been shown.
  useEffect(() => {
    if (!params.get('sso_error')) return;
    const next = new URLSearchParams(params);
    next.delete('sso_error');
    setParams(next, { replace: true });
  }, [params, setParams]);

  /*
   * Home-realm discovery: as the address is typed, ask whether its domain
   * belongs to an identity provider. Nothing is revealed for a domain that does
   * not, so this cannot be used to enumerate organisations.
   */
  useEffect(() => {
    if (mode !== 'signin' || !form.email.includes('@')) {
      setSsoOptions([]);
      return;
    }
    let cancelled = false;
    setCheckingSso(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.post<{ connections: SsoConnectionHint[] }>('/auth/sso/lookup', { email: form.email });
        if (!cancelled) setSsoOptions(result.connections);
      } catch {
        if (!cancelled) setSsoOptions([]);
      } finally {
        if (!cancelled) setCheckingSso(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.email, mode]);

  const startSso = (connectionId: string) => {
    const redirectTo = params.get('next');
    const query = new URLSearchParams({ connectionId, loginHint: form.email });
    if (redirectTo?.startsWith('/')) query.set('redirectTo', redirectTo);
    // A full navigation, not a fetch: the provider needs the browser.
    window.location.assign(`/api/auth/sso/start?${query.toString()}`);
  };

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      if (mode === 'signin') await login({ email: form.email, password: form.password });
      else await register(form);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fieldErrors);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-ink-900 p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <LogoMark className="size-9 text-white" title={APP_NAME} />
          <span className="text-lg font-semibold tracking-[-0.02em]">{APP_NAME}</span>
        </div>
        {/* The heading gets a wider measure than the body copy so "management
            system" is never split across two lines. */}
        <div className="max-w-lg">
          <h2 className="text-3xl leading-tight font-semibold">{APP_TAGLINE}</h2>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-ink-300">
            One controlled library for the policies, standards and procedures behind ISO 27001, ISO 9001, SOC 2 and the rest of
            your framework.
          </p>
          <ul className="mt-8 max-w-md space-y-4 text-[14.5px] text-ink-300">
            {[
              [
                'An editor your policy owners will use',
                'Sections, tables and cross references in a plain editor. Clause numbering and the house template look after themselves.',
              ],
              [
                'Change that has to be approved',
                'Every edit is proposed, discussed and approved against the rule you set — how many approvals, from which roles, and never your own.',
              ],
              [
                'Evidence without assembling it',
                'Compare any two issues clause by clause. Who changed what, who approved it, when it took effect, all kept as a matter of course.',
              ],
              [
                'Everyone reading the version in force',
                'A branded catalog behind your single sign-on, showing only what is published, with a PDF whenever anyone needs one.',
              ],
            ].map(([title, body]) => (
              <li key={title}>
                <p className="font-semibold text-white">{title}</p>
                <p className="mt-0.5 leading-relaxed">{body}</p>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[12.5px] text-ink-500">
          Single sign-on · role-based access · full audit trail · your own domain
        </p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-2.5 lg:hidden">
            <LogoTile size={32} />
            <span className="text-base font-semibold tracking-[-0.02em] text-ink-900">{APP_NAME}</span>
          </div>

          <h1 className="text-2xl font-semibold text-ink-900">{mode === 'signin' ? 'Sign in' : 'Create your organisation'}</h1>
          <p className="mt-1.5 text-[13.5px] text-ink-500">
            {mode === 'signin' ? 'Welcome back.' : 'You will be the owner of the new organisation.'}
          </p>

          <Card className="mt-6 p-5">
            <form onSubmit={submit} className="space-y-4">
              {error ? <Alert tone="danger">{error}</Alert> : null}

              {mode === 'register' ? (
                <>
                  <Field label="Organisation name" required error={fieldErrors.organisationName}>
                    <Input value={form.organisationName} onChange={set('organisationName')} placeholder="Northwind Trading" required autoFocus />
                  </Field>
                  <Field label="Your name" required error={fieldErrors.name}>
                    <Input value={form.name} onChange={set('name')} placeholder="Alex Owner" required autoComplete="name" />
                  </Field>
                </>
              ) : null}

              <Field label="Email address" required error={fieldErrors.email}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@organisation.com"
                  required
                  autoComplete="email"
                  autoFocus={mode === 'signin'}
                />
              </Field>

              {ssoOptions.length ? (
                <div className="space-y-2">
                  <p className="text-[13px] text-ink-600">
                    {ssoOptions[0]!.organisation} uses single sign-on. Continue with your work account:
                  </p>
                  {ssoOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant="primary"
                      size="lg"
                      className="w-full"
                      icon={<KeyRound className="size-4" />}
                      onClick={() => startSso(option.id)}
                    >
                      Continue with {option.name}
                    </Button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSsoOptions([])}
                    className="w-full text-[12.5px] text-ink-500 hover:text-brand-600"
                  >
                    Use a password instead
                  </button>
                </div>
              ) : (
                <>
                  <Field
                    label="Password"
                    required
                    error={fieldErrors.password}
                    hint={mode === 'register' ? 'At least 12 characters, with upper case, lower case and a number.' : undefined}
                  >
                    <Input
                      type="password"
                      value={form.password}
                      onChange={set('password')}
                      required
                      autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    />
                  </Field>

                  <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
                    {mode === 'signin' ? 'Sign in' : 'Create organisation'}
                  </Button>
                </>
              )}

              {checkingSso && !ssoOptions.length ? (
                <p className="flex items-center justify-center gap-1.5 text-[12px] text-ink-400">
                  <Loader2 className="size-3 animate-spin" /> Checking for single sign-on…
                </p>
              ) : null}
            </form>
          </Card>

          <p className="mt-4 text-center text-[13px] text-ink-500">
            {mode === 'signin' ? 'Need a new organisation?' : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signin' ? 'register' : 'signin');
                setError(null);
                setFieldErrors({});
              }}
              className="font-medium text-brand-600 hover:underline"
            >
              {mode === 'signin' ? 'Create one' : 'Sign in'}
            </button>
          </p>

          <p className="mt-6 text-center text-[12px] text-ink-400">
            Using single sign-on? Type your work email address and we will send you to your provider.
          </p>
        </div>
      </div>
    </div>
  );
}
